import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance, { getCurrentUserId } from '../utils/axios-auth';
import supabase from '../utils/supabase-client';
import { handleDataPersistenceError } from '../utils/errorRecovery';
// Async thunk for fetching notes
export const fetch_notes = createAsyncThunk('notes/fetch_notes', async (lesson_id, { rejectWithValue }) => {
    try {
        let endpoint = '/notes';
        if (lesson_id !== 'all') {
            endpoint = `/notes?lesson_id=${encodeURIComponent(lesson_id)}`;
        }
        const result = await handleDataPersistenceError('fetch_notes', () => axiosInstance.get(endpoint), { maxRetries: 3, baseDelay: 1000 });
        if (result.success && result.data) {
            return result.data.data;
        }
        else {
            return rejectWithValue(result.error || 'Failed to fetch notes');
        }
    }
    catch (error) {
        console.error('Error fetching notes:', error);
        let errorMessage = 'An error occurred while fetching notes';
        if (error instanceof Error) {
            errorMessage = error.message;
        }
        else if (typeof error === 'object' && error !== null && 'message' in error) {
            errorMessage = error.message;
        }
        return rejectWithValue(errorMessage);
    }
});
// Async thunk for fetching a single note by ID (for view/edit page)
export const fetch_note_by_id = createAsyncThunk('notes/fetch_note_by_id', async (noteId, { rejectWithValue }) => {
    try {
        const result = await handleDataPersistenceError('fetch_note_by_id', () => axiosInstance.get(`/notes/${noteId}`), { maxRetries: 3, baseDelay: 1000 });
        if (result.success && result.data) {
            const axiosResponse = result.data;
            const apiBody = axiosResponse?.data;
            const note = apiBody && typeof apiBody === 'object' && 'data' in apiBody
                ? apiBody.data
                : apiBody;
            if (note && typeof note === 'object' && 'id' in note) {
                return note;
            }
            return rejectWithValue('Note not found');
        }
        return rejectWithValue(result.error || 'Failed to fetch note');
    }
    catch (error) {
        console.error('Error fetching note:', error);
        let errorMessage = 'An error occurred while fetching the note';
        if (error instanceof Error) {
            errorMessage = error.message;
        }
        else if (typeof error === 'object' && error !== null && 'message' in error) {
            errorMessage = error.message;
        }
        return rejectWithValue(errorMessage);
    }
});
// Async thunk for fetching tags
export const fetch_tags = createAsyncThunk('notes/fetch_tags', async (_, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.get('/notes/tags');
        return response.data;
    }
    catch (error) {
        console.error('Error fetching tags:', error);
        let errorMessage = 'An error occurred while fetching tags';
        if (error instanceof Error) {
            errorMessage = error.message;
        }
        else if (typeof error === 'object' && error !== null && 'message' in error) {
            errorMessage = error.message;
        }
        return rejectWithValue(errorMessage);
    }
});
// Async thunk for adding a note
export const add_note = createAsyncThunk('notes/add_note', async (note, { rejectWithValue }) => {
    try {
        // Get the current authenticated user ID from Supabase
        const { data: { session } } = await supabase.auth.getSession();
        if (!session || !session.user) {
            console.error('❌ No session or user found');
            return rejectWithValue('User not authenticated. Please log in again.');
        }
        const currentUserId = session.user.id;
        if (!currentUserId) {
            console.error('❌ No current user ID found');
            return rejectWithValue('User not authenticated. Please log in again.');
        }
        // Ensure we're using the proper field names for the API
        const metadata = {
            ...note.metadata,
            tags: note.metadata?.tags || [],
            note_type: note.metadata?.note_type || 'bullet',
            media_links: note.metadata?.media_links || [],
        };
        // Only include linked_note_ids if there are values to avoid server-side validation on empty arrays
        if (note.metadata?.linked_note_ids && note.metadata.linked_note_ids.length > 0) {
            metadata.linked_note_ids = note.metadata.linked_note_ids;
        }
        const payload = {
            ...note,
            // Ensure student_id matches the authenticated user's ID for RLS
            student_id: currentUserId,
            // Make sure metadata is properly structured
            metadata,
        };
        // Log the final payload for debugging
        const response = await axiosInstance.post('/notes', payload);
        return response.data;
    }
    catch (error) {
        console.error('❌ Error adding note:', error);
        let errorMessage = 'An error occurred while adding the note';
        if (error instanceof Error) {
            errorMessage = error.message;
        }
        else if (typeof error === 'object' && error !== null && 'message' in error) {
            errorMessage = error.message;
        }
        return rejectWithValue(errorMessage);
    }
});
// Async thunk for updating a note
export const update_note = createAsyncThunk('notes/update_note', async (note, { rejectWithValue }) => {
    try {
        // Get the current authenticated user ID from Supabase
        const currentUserId = await getCurrentUserId();
        if (!currentUserId) {
            console.error('❌ No current user ID found for update');
            return rejectWithValue('User not authenticated. Please log in again.');
        }
        // Ensure we're using the proper field names for the API
        const payload = {
            ...note,
            // Ensure student_id matches the authenticated user's ID for RLS
            student_id: currentUserId,
        };
        const response = await axiosInstance.put(`/notes/${note.id}`, payload);
        return response.data;
    }
    catch (error) {
        console.error('❌ Error updating note:', error);
        let errorMessage = 'An error occurred while updating the note';
        if (error instanceof Error) {
            errorMessage = error.message;
        }
        else if (typeof error === 'object' && error !== null && 'message' in error) {
            errorMessage = error.message;
        }
        return rejectWithValue(errorMessage);
    }
});
// Async thunk for deleting a note
export const delete_note = createAsyncThunk('notes/delete_note', async (note_id, { rejectWithValue }) => {
    try {
        // Check if we have a valid note_id
        if (!note_id) {
            throw new Error('Note ID is required');
        }
        // Make the delete request with full URL for debugging
        const fullUrl = `${axiosInstance.defaults.baseURL}/notes/${note_id}`;
        // Add authentication debugging
        const { data: { session } } = await supabase.auth.getSession();
        const response = await axiosInstance.delete(`/notes/${note_id}`);
        // Verify the response
        if (response.data && response.data.success === false) {
            throw new Error(response.data.message || 'Delete operation failed');
        }
        return note_id;
    }
    catch (error) {
        console.error('Error deleting note:', error);
        let errorMessage = 'An error occurred while deleting the note';
        if (error instanceof Error) {
            errorMessage = error.message;
        }
        else if (typeof error === 'object' && error !== null && 'message' in error) {
            errorMessage = error.message;
        }
        // Log additional error details for debugging
        if (error && typeof error === 'object' && 'response' in error) {
            const axiosError = error;
            console.error('Axios error details:', {
                status: axiosError.response?.status,
                statusText: axiosError.response?.statusText,
                data: axiosError.response?.data,
                url: axiosError.config?.url,
                method: axiosError.config?.method,
                headers: axiosError.config?.headers
            });
            // Handle specific HTTP status codes
            if (axiosError.response?.status === 401) {
                errorMessage = 'Authentication required. Please log in again.';
            }
            else if (axiosError.response?.status === 403) {
                errorMessage = 'Not authorized to delete this note.';
            }
            else if (axiosError.response?.status === 404) {
                errorMessage = 'Note not found.';
            }
            else if (axiosError.response?.status === 500) {
                errorMessage = 'Server error occurred while deleting the note.';
            }
            // Extract more specific error message from response
            if (axiosError.response?.data?.message) {
                errorMessage = axiosError.response.data.message;
            }
            else if (axiosError.response?.data?.error) {
                errorMessage = axiosError.response.data.error;
            }
        }
        return rejectWithValue(errorMessage);
    }
});
// Async thunk for linking notes
export const link_notes = createAsyncThunk('notes/link_notes', async ({ note_id1, note_id2 }, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.post(`/notes/${note_id1}/link/${note_id2}`);
        return response.data;
    }
    catch (error) {
        console.error('Error linking notes:', error);
        let errorMessage = 'An error occurred while linking notes';
        if (error instanceof Error) {
            errorMessage = error.message;
        }
        else if (typeof error === 'object' && error !== null && 'message' in error) {
            errorMessage = error.message;
        }
        return rejectWithValue(errorMessage);
    }
});
// Create the slice
const note_slice = createSlice({
    name: 'notes',
    initialState: {
        items: [],
        tags: [],
        status: 'idle',
        error: null
    },
    reducers: {},
    extraReducers: (builder) => {
        builder
            // Fetch notes cases
            .addCase(fetch_notes.pending, (state) => {
            state.status = 'loading';
        })
            .addCase(fetch_notes.fulfilled, (state, action) => {
            state.status = 'succeeded';
            // Handle different response structures
            if (action.payload && typeof action.payload === 'object') {
                if ('data' in action.payload) {
                    // Handle {success, count, data} structure
                    state.items = action.payload.data;
                }
                else if (Array.isArray(action.payload)) {
                    // Handle array structure directly
                    state.items = action.payload;
                }
                else {
                    // Unknown structure
                    console.error('Unexpected notes data structure:', action.payload);
                    state.items = [];
                }
            }
            else {
                state.items = [];
            }
            state.error = null;
        })
            .addCase(fetch_notes.rejected, (state, action) => {
            state.status = 'failed';
            state.error = action.payload ?? 'Unknown error occurred';
        })
            // Fetch tags cases
            .addCase(fetch_tags.pending, (state) => {
            state.status = 'loading';
        })
            .addCase(fetch_tags.fulfilled, (state, action) => {
            state.status = 'succeeded';
            state.tags = action.payload;
            state.error = null;
        })
            .addCase(fetch_tags.rejected, (state, action) => {
            state.status = 'failed';
            state.error = action.payload ?? 'Unknown error occurred';
        })
            // Add note cases
            .addCase(add_note.pending, (state, action) => {
            state.status = 'loading';
            // Optimistically add note to state
            const tempNote = {
                ...action.meta.arg,
                id: `temp-${Date.now()}`,
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString()
            };
            state.items.push(tempNote);
        })
            .addCase(add_note.fulfilled, (state, action) => {
            state.status = 'succeeded';
            // Handle the API response structure {success: true, data: note}
            const noteData = action.payload.data || action.payload;
            // Replace temp note with real one
            const tempIndex = state.items.findIndex(note => note.id?.startsWith('temp-'));
            if (tempIndex !== -1) {
                state.items[tempIndex] = noteData;
            }
            else {
                state.items.push(noteData);
            }
            // Update tags if new tags were added
            if (noteData.metadata?.tags) {
                const new_tags = noteData.metadata.tags.filter((tag) => !state.tags.includes(tag));
                state.tags.push(...new_tags);
            }
            state.error = null;
        })
            .addCase(add_note.rejected, (state, action) => {
            state.status = 'failed';
            state.error = action.payload ?? 'Unknown error occurred';
            // Remove temp note on failure
            state.items = state.items.filter(note => !note.id?.startsWith('temp-'));
        })
            // Update note cases
            .addCase(update_note.pending, (state, action) => {
            state.status = 'loading';
            // Optimistically update note in state
            const updateData = action.meta.arg;
            const index = state.items.findIndex(note => note.id === updateData.id);
            if (index !== -1) {
                state.items[index] = {
                    ...state.items[index],
                    ...updateData,
                    updated_at: new Date().toISOString()
                };
            }
        })
            .addCase(update_note.fulfilled, (state, action) => {
            state.status = 'succeeded';
            // Handle the API response structure {success: true, data: note}
            const noteData = action.payload.data || action.payload;
            const index = state.items.findIndex(note => note.id === noteData.id);
            if (index !== -1) {
                state.items[index] = noteData;
                // Update tags if tags were changed
                if (noteData.metadata?.tags) {
                    const new_tags = noteData.metadata.tags.filter((tag) => !state.tags.includes(tag));
                    state.tags.push(...new_tags);
                }
            }
            state.error = null;
        })
            .addCase(update_note.rejected, (state, action) => {
            state.status = 'failed';
            state.error = action.payload ?? 'Unknown error occurred';
            // Revert optimistic update on failure
            const updateData = action.meta.arg;
            const index = state.items.findIndex(note => note.id === updateData.id);
            if (index !== -1) {
                // Trigger a refetch to get the correct state
                state.status = 'idle';
            }
        })
            // Delete note cases
            .addCase(delete_note.pending, (state) => {
            state.status = 'loading';
        })
            .addCase(delete_note.fulfilled, (state, action) => {
            state.status = 'succeeded';
            // Remove the note from the state
            const deletedNoteId = action.payload;
            const originalLength = state.items.length;
            state.items = state.items.filter(note => note.id !== deletedNoteId);
            // Log if the note wasn't found in state (might have been already removed)
            if (state.items.length === originalLength) {
                console.warn(`Note with ID ${deletedNoteId} was not found in state during deletion`);
            }
            state.error = null;
        })
            .addCase(delete_note.rejected, (state, action) => {
            state.status = 'failed';
            state.error = action.payload ?? 'Unknown error occurred';
            console.error('Delete note failed:', action.payload);
        })
            // Link notes cases
            .addCase(link_notes.pending, (state) => {
            state.status = 'loading';
        })
            .addCase(link_notes.fulfilled, (state, action) => {
            state.status = 'succeeded';
            // Update both linked notes in the state
            action.payload.forEach(updated_note => {
                const index = state.items.findIndex(note => note.id === updated_note.id);
                if (index !== -1) {
                    state.items[index] = updated_note;
                }
            });
            state.error = null;
        })
            .addCase(link_notes.rejected, (state, action) => {
            state.status = 'failed';
            state.error = action.payload ?? 'Unknown error occurred';
        });
    },
});
export default note_slice.reducer;
